import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ChatMessage from "../ChatMessage/ChatMessage";
import ConversationBar from "../ConversationBar/ConversationBar";
import {
    sendMessage,
    sendFileMessage,
    getConversations,
    createConversation,
    deleteConversation
} from "../../api/chatService";
import { translations } from "../../utils/translations";
import "./RepliAssistantFloating.css";

const RepliAssistantFloating = ({ user }) => {
    const [isChatVisible, setIsChatVisible] = useState(false);
    const [showTooltip, setShowTooltip] = useState(true);
    const [chatHistory, setChatHistory] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const chatContainerRef = useRef(null);
    const windowRef = useRef(null);
    const abortControllerRef = useRef(null);

    const location = useLocation();
    const isExcludedRoute = location.pathname === '/' || location.pathname.startsWith('/profile') || location.pathname.startsWith('/social') || location.pathname === '/explore';

    const extraClass = location.pathname === '/replikers' ? 'on-left' : '';

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowTooltip(false);
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    const currentLang = localStorage.getItem("language") || "es";
    const t = translations[currentLang]?.home || translations["es"].home;

    useEffect(() => {
        const initConversation = async () => {
            if (user && !isExcludedRoute) {
                try {
                    // Logic for Default Chat
                    const convs = await getConversations("default");
                    if (convs && convs.length > 0) {
                        setConversationId(convs[0].id);
                        setChatHistory(convs[0].history);
                    } else {
                        const newConv = await createConversation("default", "Home Chat");
                        setConversationId(newConv.id);
                        setChatHistory([]);
                    }
                } catch (error) {
                    console.error("Error initializing floating assistant conversation:", error);
                }
            }
        };

        // Reload conversation when chat becomes visible on valid routes
        if (isChatVisible) {
            initConversation();
        }
    }, [user, isChatVisible, isExcludedRoute]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatHistory, isChatVisible]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (windowRef.current && !windowRef.current.contains(event.target)) {
                setIsChatVisible(false);
            }
        };

        if (isChatVisible) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isChatVisible]);

    const handleCancel = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsLoading(false);
    };

    const handleNewUserMessage = async (messageObject) => {
        const { text, file } = messageObject;
        if (!text.trim() && !file) return;

        setChatHistory((prev) => [
            ...prev,
            messageObject,
            { sender: "bot", text: "", thinking: true, streaming: true, status: t.thinking },
        ]);
        setIsLoading(true);

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        try {
            // Sending to Default Chat
            let currentConversationId = conversationId;
            if (!currentConversationId && user) {
                const newConv = await createConversation("default", "Home Chat");
                currentConversationId = newConv.id;
                setConversationId(newConv.id);
            }

            if (file) {
                let streamedFileText = "";
                const response = await sendFileMessage(
                    file,
                    text,
                    "default",
                    currentConversationId,
                    signal
                );

                if (response && response.history) {
                    setChatHistory(response.history);
                } else if (response && response.message) {
                    setChatHistory((prev) => {
                        const cleanHistory = prev.filter((msg) => !msg.thinking);
                        return [...cleanHistory, { sender: "bot", text: response.message }];
                    });
                } else {
                    setChatHistory((prev) => prev.filter((msg) => !msg.thinking));
                }
            } else {
                const response = await sendMessage(
                    text,
                    "default",
                    currentConversationId,
                    signal
                );

                if (response && response.history) {
                    setChatHistory(response.history);
                }
            }
        } catch (error) {
            if (error.name === "CanceledError" || axios.isCancel(error)) {
                setChatHistory((prev) => prev.filter(m => !m.thinking));
            } else {
                const errorMessage = { sender: "bot", text: t.errorConnect };
                setChatHistory((prev) => [...prev.filter(m => !m.thinking), errorMessage]);
            }
        } finally {
            setIsLoading(false);
            setSelectedFile(null); // Clear selected file after sending
            abortControllerRef.current = null;
        }
    };

    const handleClearChat = async () => {
        if (window.confirm(t.confirmClearChat || "¿Estás seguro de que quieres limpiar la conversación?")) {
            try {
                // Logic for Default Chat (SQL-based)
                if (conversationId) {
                    await deleteConversation(conversationId);
                    setConversationId(null); // Reset ID so next msg creates new one
                    setChatHistory([]); // Clear visual
                }
            } catch (error) {
                console.error("Error clearing chat history:", error);
            }
        }
    };

    // SIEMPRE OCULTAR EN PROFILE Y SOCIAL
    if (isExcludedRoute) return null;

    return (
        <div className={`repli-assistant-floating ${extraClass} ${isChatVisible ? "active" : ""}`}>
            {isChatVisible && (
                <div className="floating-chat-window" ref={windowRef}>
                    <div className="floating-chat-header">
                        <div className="header-left">
                            <img src="/InsightRepli.ico" alt="Repliker" className="repli-mini-icon-img" />
                            <span>Repli Assistant</span>
                        </div>
                        <div className="header-right">
                            <button className="clean-btn" onClick={handleClearChat} title="Limpiar">
                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T720-120H280Zm440-600H240v520q0 8 6 14t14 6h440q8 0 14-6t6-14v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM240-720v520-520Z" /></svg>
                            </button>
                            <button className="close-btn" onClick={() => setIsChatVisible(false)}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" /></svg>
                            </button>
                        </div>
                    </div>
                    <div className="floating-chat-messages" ref={chatContainerRef}>
                        {chatHistory.length === 0 && (
                            <div className="empty-chat-msg">{t.searchPlaceholder}</div>
                        )}
                        {chatHistory
                            .filter(msg => msg && msg.sender) // Defensive check
                            .map((msg, index) => (
                                <ChatMessage
                                    key={index}
                                    sender={msg.sender}
                                    text={msg.text}
                                    file={msg.file}
                                />
                            ))}
                        {isLoading && (
                            <ChatMessage
                                sender="bot"
                                text={t.thinking}
                                thinking={true}
                            />
                        )}
                    </div>
                    <div className="floating-chat-footer">
                        <ConversationBar
                            onNewUserMessage={handleNewUserMessage}
                            onCancel={handleCancel}
                            isLoading={isLoading}
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                        />
                    </div>
                </div>
            )}

            {!isChatVisible && (
                <button
                    className="floating-trigger-btn"
                    onClick={() => setIsChatVisible(true)}
                    title="Hablar con Repli"
                >
                    {showTooltip && (
                        <div className="repli-tooltip">
                            Hola, estoy aquí, soy Repli
                        </div>
                    )}
                    <img src="/InsightRepli.ico" alt="Repli" className="trigger-icon-img" />
                    <span className="notification-dot"></span>
                </button>
            )}
        </div>
    );
};

export default RepliAssistantFloating;
